import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    Button,
    Modal
} from "react-bootstrap";

import { fetchExperiences, createExperience } from '../../../redux/actions/experiences';
import ModernExperienceCard from './components/ModernExperienceCard.jsx';
import ExperienceForm from './components/ExperienceForm.jsx';

class ExperiencesHome extends Component {
    constructor(props) {
        super(props);
        
        this.state = {
            showCreateModal: false,
            activeTab: 'recent'
        };
    }

    componentDidMount() {
        // Charger les expériences au démarrage
        this.props.fetchExperiences();
    }

    handleShowCreateModal = () => {
        this.setState({ showCreateModal: true });
    };

    handleCloseCreateModal = () => {
        this.setState({ showCreateModal: false });
    };

    handleCreateExperience = async (experienceData) => {
        try {
            await this.props.createExperience(experienceData);
            this.handleCloseCreateModal();

            // Afficher notification de succès
            if (this.props.showNotification) {
                this.props.showNotification("Expérience partagée avec succès !", "success");
            }
        } catch (error) {
            if (this.props.showNotification) {
                this.props.showNotification("Erreur lors du partage de l'expérience", "error");
            }
        }
    };

    handleTabSelect = (tabKey) => {
        this.setState({ activeTab: tabKey });
    };

    getFilteredExperiences = () => {
        const { experiences } = this.props;
        const { activeTab } = this.state;

        switch (activeTab) {
            case 'recent':
                return [...experiences].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            case 'popular':
                return [...experiences].sort((a, b) => {
                    const aReactions = Object.values(a.reactions || {}).reduce((sum, count) => sum + count, 0);
                    const bReactions = Object.values(b.reactions || {}).reduce((sum, count) => sum + count, 0);
                    return bReactions - aReactions;
                });
            case 'followed':
                // Pour l'instant, retourner toutes les expériences
                return experiences;
            default:
                return experiences;
        }
    };

    render() {
        const { loading, user } = this.props;
        const { showCreateModal, activeTab } = this.state;
        const filteredExperiences = this.getFilteredExperiences();

        return (
            <div className="experiences-page">
                {/* Header avec gradient moderne */}
                <div className="hero-section">
                    <div className="hero-content">
                        {user && (
                            <div className="hero-action">
                                <Button
                                    onClick={this.handleShowCreateModal}
                                    className="create-experience-btn"
                                >
                                    <span className="btn-icon">✨</span>
                                    Partager mon expérience
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Filtres modernes */}
                <div className="filters-section">
                    <div className="filters-container">
                        <div className="filter-tabs">
                            <button
                                className={`filter-tab ${activeTab === 'recent' ? 'active' : ''}`}
                                onClick={() => this.handleTabSelect('recent')}
                            >
                                <span className="tab-icon">🕒</span>
                                <span>Récentes</span>
                            </button>
                            <button
                                className={`filter-tab ${activeTab === 'popular' ? 'active' : ''}`}
                                onClick={() => this.handleTabSelect('popular')}
                            >
                                <span className="tab-icon">⭐</span>
                                <span>Populaires</span>
                            </button>
                            <button
                                className={`filter-tab ${activeTab === 'followed' ? 'active' : ''}`}
                                onClick={() => this.handleTabSelect('followed')}
                            >
                                <span className="tab-icon">❤️</span>
                                <span>Suivies</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Contenu principal */}
                <div className="content-section">
                    <div className="content-container">
                        {loading && (
                            <div className="loading-state">
                                <div className="loading-spinner">
                                    <div className="spinner"></div>
                                </div>
                                <h3>Chargement des expériences...</h3>
                                <p>Récupération des témoignages de la communauté</p>
                            </div>
                        )}

                        {!loading && filteredExperiences.length === 0 && (
                            <div className="empty-state">
                                <div className="empty-icon">📖</div>
                                <h3>Aucune expérience partagée</h3>
                                <p>Soyez le premier à enrichir notre base de connaissances !</p>
                                {user && (
                                    <Button
                                        onClick={this.handleShowCreateModal}
                                        className="empty-action-btn"
                                    >
                                        Commencer à partager
                                    </Button>
                                )}
                            </div>
                        )}

                        {!loading && filteredExperiences.length > 0 && (
                            <div className="experiences-grid">
                                {filteredExperiences.map(experience => (
                                    <ModernExperienceCard
                                        key={experience.id}
                                        experience={experience}
                                        currentUser={user}
                                        showNotification={this.props.showNotification}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal pour créer une nouvelle expérience */}
                <Modal
                    show={showCreateModal}
                    onHide={this.handleCloseCreateModal}
                    bsSize="large"
                    className="modern-modal experience-modal-xl"
                >
                    <Modal.Header closeButton className="modal-header">
                        <Modal.Title className="modal-title">
                            Partager une nouvelle expérience
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="modal-body">
                        <ExperienceForm
                            onSubmit={this.handleCreateExperience}
                            onCancel={this.handleCloseCreateModal}
                        />
                    </Modal.Body>
                </Modal>

                <style jsx>{`
                    .experiences-page {
                        min-height: 100vh;
                        background: #ffffff;
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    }

                    .hero-section {
                        padding: 80px 20px 60px;
                        position: relative;
                        overflow: hidden;
                    }

                    .hero-section::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="80" cy="20" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="20" cy="80" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="80" cy="80" r="1" fill="rgba(255,255,255,0.1)"/></svg>') repeat;
                        animation: float 20s ease-in-out infinite;
                    }

                    @keyframes float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-20px); }
                    }

                    .hero-content {
                        max-width: 1200px;
                        margin: 0 auto;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 60px;
                        position: relative;
                        z-index: 2;
                    }

                    .hero-text {
                        flex: 1;
                        color: #2c3e50;
                    }

                    .main-title {
                        font-size: 3.5rem;
                        font-weight: 800;
                        margin: 0 0 25px 0;
                        line-height: 1.2;
                        text-shadow: 0 2px 8px rgba(44, 62, 80, 0.1);
                        color: #2c3e50;
                    }

                    .title-icon {
                        display: inline-block;
                        margin-right: 15px;
                        font-size: 3.5rem;
                        animation: pulse 2s infinite;
                    }

                    @keyframes pulse {
                        0%, 100% { transform: scale(1); }
                        50% { transform: scale(1.1); }
                    }

                    .subtitle {
                        font-size: 1.4rem;
                        line-height: 1.6;
                        margin-bottom: 30px;
                        opacity: 0.9;
                        font-weight: 400;
                        color: #7f8c8d;
                    }

                    .benefits-list {
                        display: flex;
                        gap: 30px;
                        flex-wrap: wrap;
                    }

                    .benefit-item {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        background: #f8f9fa;
                        padding: 12px 20px;
                        border-radius: 25px;
                        border: 1px solid #e9ecef;
                        font-weight: 600;
                        color: #2c3e50;
                    }

                    .benefit-icon {
                        font-size: 1.3rem;
                    }

                    .hero-action {
                        flex-shrink: 0;
                    }

                    .create-experience-btn {
                        background: linear-gradient(135deg, #42a5f5 0%, #2196f3 100%);
                        border: none;
                        padding: 20px 35px;
                        border-radius: 30px;
                        color: white;
                        font-size: 1.2rem;
                        font-weight: 700;
                        display: flex;
                        align-items: center;
                        gap: 15px;
                        box-shadow: 0 10px 30px rgba(66, 165, 245, 0.4);
                        transition: all 0.3s ease;
                        text-transform: none;
                        letter-spacing: 0.5px;
                    }

                    .create-experience-btn:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 20px 40px rgba(66, 165, 245, 0.6);
                        background: linear-gradient(135deg, #2196f3 0%, #42a5f5 100%);
                    }

                    .btn-icon {
                        font-size: 1.5rem;
                        animation: sparkle 1.5s ease-in-out infinite;
                    }

                    @keyframes sparkle {
                        0%, 100% { transform: rotate(0deg) scale(1); }
                        25% { transform: rotate(-5deg) scale(1.1); }
                        75% { transform: rotate(5deg) scale(1.1); }
                    }

                    .filters-section {
                        background: #f8f9fa;
                        border-top: 1px solid #e9ecef;
                        padding: 25px 0;
                    }

                    .filters-container {
                        max-width: 1200px;
                        margin: 0 auto;
                        padding: 0 20px;
                    }

                    .filter-tabs {
                        display: flex;
                        justify-content: center;
                        gap: 15px;
                        flex-wrap: wrap;
                    }

                    .filter-tab {
                        background: transparent;
                        border: 2px solid #e2e8f0;
                        padding: 15px 25px;
                        border-radius: 20px;
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        color: #64748b;
                        font-weight: 600;
                        font-size: 1rem;
                        transition: all 0.3s ease;
                        cursor: pointer;
                    }

                    .filter-tab:hover {
                        border-color: #42a5f5;
                        color: #42a5f5;
                        transform: translateY(-2px);
                        box-shadow: 0 5px 15px rgba(66, 165, 245, 0.2);
                    }

                    .filter-tab.active {
                        background: linear-gradient(135deg, #42a5f5 0%, #2196f3 100%);
                        border-color: transparent;
                        color: white;
                        transform: translateY(-2px);
                        box-shadow: 0 10px 25px rgba(66, 165, 245, 0.4);
                    }

                    .tab-icon {
                        font-size: 1.2rem;
                    }

                    .content-section {
                        background: #f8fafc;
                        min-height: 60vh;
                        padding: 50px 0;
                    }

                    .content-container {
                        max-width: 1200px;
                        margin: 0 auto;
                        padding: 0 20px;
                    }

                    .loading-state, .empty-state {
                        text-align: center;
                        padding: 80px 20px;
                        background: white;
                        border-radius: 25px;
                        box-shadow: 0 10px 40px rgba(0,0,0,0.08);
                        border: 1px solid #f1f5f9;
                    }

                    .loading-spinner {
                        margin-bottom: 30px;
                    }

                    .spinner {
                        width: 60px;
                        height: 60px;
                        border: 4px solid #e3f2fd;
                        border-left: 4px solid #42a5f5;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        margin: 0 auto;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    .loading-state h3, .empty-state h3 {
                        color: #1e293b;
                        font-size: 1.8rem;
                        margin-bottom: 15px;
                        font-weight: 700;
                    }

                    .loading-state p, .empty-state p {
                        color: #64748b;
                        font-size: 1.1rem;
                        margin-bottom: 30px;
                    }

                    .empty-icon {
                        font-size: 4rem;
                        margin-bottom: 25px;
                        opacity: 0.8;
                    }

                    .empty-action-btn {
                        background: linear-gradient(135deg, #42a5f5 0%, #2196f3 100%);
                        border: none;
                        padding: 15px 30px;
                        border-radius: 25px;
                        color: white;
                        font-weight: 600;
                        font-size: 1.1rem;
                    }

                    .experiences-grid {
                        display: grid;
                        gap: 30px;
                        grid-template-columns: 1fr;
                    }

                    .guidelines-section {
                        background: white;
                        padding: 60px 0;
                    }

                    .guidelines-container {
                        max-width: 1200px;
                        margin: 0 auto;
                        padding: 0 20px;
                        text-align: center;
                    }

                    .guidelines-container h3 {
                        font-size: 2.2rem;
                        color: #1e293b;
                        margin-bottom: 40px;
                        font-weight: 700;
                    }

                    .guidelines-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                        gap: 30px;
                        margin-top: 40px;
                    }

                    .guideline-card {
                        background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                        padding: 35px 25px;
                        border-radius: 20px;
                        border: 2px solid #f1f5f9;
                        transition: all 0.3s ease;
                        text-align: center;
                    }

                    .guideline-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 15px 35px rgba(0,0,0,0.1);
                        border-color: #e2e8f0;
                    }

                    .guideline-icon {
                        font-size: 3rem;
                        margin-bottom: 20px;
                    }

                    .guideline-card h4 {
                        color: #1e293b;
                        font-size: 1.3rem;
                        margin-bottom: 15px;
                        font-weight: 700;
                    }

                    .guideline-card p {
                        color: #64748b;
                        font-size: 1rem;
                        line-height: 1.6;
                        margin: 0;
                    }

                    /* Modal styles */
                    :global(.modern-modal .modal-dialog) {
                        max-width: 900px;
                        margin: 30px auto;
                    }

                    :global(.experience-modal-xl .modal-dialog) {
                        max-width: 1100px !important;
                        width: 95vw;
                        margin: 20px auto;
                    }

                    :global(.modern-modal .modal-content) {
                        border: none;
                        border-radius: 25px;
                        box-shadow: 0 25px 50px rgba(0,0,0,0.2);
                        overflow: hidden;
                    }

                    :global(.experience-modal-xl .modal-content) {
                        min-height: 80vh;
                    }

                    .modal-header {
                        background: linear-gradient(135deg, #42a5f5 0%, #2196f3 100%);
                        color: white;
                        padding: 30px 40px;
                        border: none;
                    }

                    .modal-title {
                        font-size: 1.8rem;
                        font-weight: 700;
                        display: flex;
                        align-items: center;
                        gap: 15px;
                    }

                    .modal-icon {
                        font-size: 2.2rem;
                    }

                    .modal-body {
                        padding: 40px 45px;
                        background: #fafbfc;
                    }

                    .form-intro {
                        background: #e6fffa;
                        border: 1px solid #81e6d9;
                        border-radius: 12px;
                        padding: 25px;
                        margin-bottom: 30px;
                    }

                    .form-intro p {
                        margin: 0;
                        color: #234e52;
                        font-size: 1.1rem;
                        line-height: 1.6;
                        font-weight: 500;
                    }

                    /* Responsive design */
                    @media (max-width: 768px) {
                        .hero-content {
                            flex-direction: column;
                            text-align: center;
                            gap: 40px;
                        }

                        .main-title {
                            font-size: 2.5rem;
                        }

                        .subtitle {
                            font-size: 1.2rem;
                        }

                        .benefits-list {
                            justify-content: center;
                        }

                        .filter-tabs {
                            flex-direction: column;
                            align-items: center;
                        }

                        .filter-tab {
                            min-width: 200px;
                            justify-content: center;
                        }

                        .guidelines-grid {
                            grid-template-columns: 1fr;
                        }
                    }

                    .experiences-page {
                        min-height: 100vh;
                        color: #263746;
                        background: #f6f9fa;
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
                    }

                    .experiences-page .hero-section {
                        max-width: 1600px;
                        padding: 18px 32px 16px;
                        margin: 0 auto;
                        background: transparent;
                    }

                    .experiences-page .hero-section::before {
                        display: none;
                    }

                    .experiences-page .hero-content {
                        max-width: none;
                        justify-content: flex-end;
                        gap: 20px;
                        padding: 18px 22px;
                        background: linear-gradient(120deg, #fff 0%, #f2fbfd 100%);
                        border: 1px solid #e4eef1;
                        border-radius: 18px;
                        box-shadow: 0 8px 24px rgba(31, 58, 69, 0.05);
                    }

                    .experiences-page .hero-text {
                        color: #263746;
                    }

                    .experiences-page .main-title {
                        margin: 0 0 12px;
                        color: #17313d;
                        font-size: clamp(25px, 2.4vw, 34px);
                        font-weight: 700;
                        letter-spacing: -0.5px;
                        line-height: 1.25;
                        text-shadow: none;
                    }

                    .experiences-page .title-icon {
                        margin-right: 10px;
                        color: #087f95;
                        font-size: 27px;
                        animation: none;
                    }

                    .experiences-page .subtitle {
                        max-width: 780px;
                        margin-bottom: 18px;
                        color: #64748b;
                        font-size: 14px;
                        line-height: 1.65;
                    }

                    .experiences-page .benefits-list {
                        gap: 8px;
                    }

                    .experiences-page .benefit-item {
                        gap: 8px;
                        padding: 8px 12px;
                        color: #47606c;
                        background: #fff;
                        border: 1px solid #e2ecef;
                        border-radius: 8px;
                        font-size: 12px;
                        font-weight: 600;
                    }

                    .experiences-page .benefit-icon {
                        font-size: 14px;
                    }

                    .experiences-page .hero-action {
                        flex-shrink: 0;
                    }

                    .experiences-page .create-experience-btn,
                    .experiences-page .create-experience-btn:hover,
                    .experiences-page .create-experience-btn:focus {
                        min-height: 46px;
                        padding: 12px 18px;
                        color: #073b46;
                        background: var(--app-primary-color);
                        border: 1px solid var(--app-primary-color);
                        border-radius: 9px;
                        box-shadow: 0 5px 14px rgba(var(--app-primary-rgb), 0.2);
                        font-size: 14px;
                        font-weight: 600;
                        letter-spacing: 0;
                        transition: box-shadow 0.18s ease, transform 0.18s ease;
                    }

                    .experiences-page .create-experience-btn:hover {
                        transform: translateY(-1px);
                        box-shadow: 0 7px 18px rgba(var(--app-primary-rgb), 0.28);
                    }

                    .experiences-page .btn-icon {
                        font-size: 15px;
                        animation: none;
                    }

                    .experiences-page .filters-section {
                        padding: 0 32px;
                        background: transparent;
                        border: 0;
                    }

                    .experiences-page .filters-container,
                    .experiences-page .content-container {
                        max-width: 1600px;
                        padding: 0;
                    }

                    .experiences-page .filter-tabs {
                        justify-content: flex-start;
                        gap: 8px;
                    }

                    .experiences-page .filter-tab {
                        padding: 10px 15px;
                        color: #526574;
                        background: #fff;
                        border: 1px solid #dfe8eb;
                        border-radius: 9px;
                        font-size: 13px;
                    }

                    .experiences-page .filter-tab:hover {
                        color: #087f95;
                        border-color: var(--app-primary-color);
                        box-shadow: none;
                        transform: translateY(-1px);
                    }

                    .experiences-page .filter-tab.active {
                        color: #073b46;
                        background: var(--app-primary-color);
                        border-color: var(--app-primary-color);
                        box-shadow: 0 4px 12px rgba(var(--app-primary-rgb), 0.2);
                        transform: none;
                    }

                    .experiences-page .tab-icon {
                        font-size: 14px;
                    }

                    .experiences-page .content-section {
                        min-height: 50vh;
                        padding: 24px 32px 48px;
                        background: transparent;
                    }

                    .experiences-page .loading-state,
                    .experiences-page .empty-state {
                        padding: 52px 24px;
                        border: 1px solid #e3ebee;
                        border-radius: 16px;
                        box-shadow: 0 8px 24px rgba(31, 58, 69, 0.05);
                    }

                    .experiences-page .spinner {
                        border-color: var(--app-primary-tint);
                        border-left-color: var(--app-primary-color);
                    }

                    .experiences-page .loading-state h3,
                    .experiences-page .empty-state h3 {
                        font-size: 20px;
                    }

                    .experiences-page .loading-state p,
                    .experiences-page .empty-state p {
                        font-size: 14px;
                    }

                    .experiences-page .empty-icon {
                        margin-bottom: 16px;
                        color: #087f95;
                        font-size: 42px;
                    }

                    .experiences-page .empty-action-btn {
                        min-height: 44px;
                        padding: 11px 18px;
                        color: #073b46;
                        background: var(--app-primary-color);
                        border: 0;
                        border-radius: 9px;
                        font-size: 14px;
                    }

                    .experiences-page .experiences-grid {
                        gap: 18px;
                    }

                    .experiences-page .experience-modal-xl .modal-header {
                        color: #073b46;
                        background: var(--app-primary-color);
                    }

                    @media (max-width: 768px) {
                        .experiences-page .hero-section {
                            padding: 14px 14px 12px;
                        }

                        .experiences-page .hero-content {
                            align-items: flex-start;
                            gap: 20px;
                            padding: 16px 18px;
                            text-align: left;
                        }

                        .experiences-page .hero-text {
                            text-align: left;
                        }

                        .experiences-page .main-title {
                            font-size: 24px;
                        }

                        .experiences-page .subtitle {
                            font-size: 13px;
                        }

                        .experiences-page .benefits-list {
                            justify-content: flex-start;
                        }

                        .experiences-page .hero-action {
                            width: 100%;
                        }

                        .experiences-page .create-experience-btn {
                            justify-content: center;
                            width: 100%;
                        }

                        .experiences-page .filters-section {
                            padding: 0 14px;
                        }

                        .experiences-page .filter-tabs {
                            justify-content: flex-start;
                            flex-direction: row;
                            overflow-x: auto;
                            padding-bottom: 4px;
                        }

                        .experiences-page .filter-tab {
                            flex: 0 0 auto;
                            min-width: 0;
                            justify-content: center;
                        }

                        .experiences-page .content-section {
                            padding: 18px 14px 32px;
                        }
                    }
                `}</style>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    experiences: state.experiences.experiences,
    loading: state.experiences.loading,
    user: state.user.user || state.user.offlineUser
});

const mapDispatchToProps = dispatch => ({
    fetchExperiences: () => dispatch(fetchExperiences()),
    createExperience: (experienceData) => dispatch(createExperience(experienceData))
});

ExperiencesHome.propTypes = {
    experiences: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    user: PropTypes.object,
    fetchExperiences: PropTypes.func.isRequired,
    createExperience: PropTypes.func.isRequired,
    showNotification: PropTypes.func
};

export default connect(mapStateToProps, mapDispatchToProps)(ExperiencesHome);